import {
    faBinoculars,
    faCamera,
    faEarthAmericas,
    faFeatherPointed,
    faLeaf,
    faLocationDot,
    faMountainSun
} from "@fortawesome/free-solid-svg-icons";
import type {AchievementDashboard, AchievementSetDefinition} from "./types";

const createMilestone = (
    prefix: string,
    threshold: number,
    description: string,
    rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary',
    points: number,
    title?: string
) => ({
    id: `${prefix}-${threshold}`,
    title: title ?? `${threshold.toLocaleString()} ${description}`,
    description: `Reach ${threshold.toLocaleString()} ${description.toLowerCase()}.`,
    threshold,
    rarity,
    points
});

export const ACHIEVEMENT_THEME_LABELS: AchievementDashboard['themeOptions'] = [
    {id: 'all', label: 'All Themes'},
    {id: 'species', label: 'Species'},
    {id: 'habitat', label: 'Habitats'},
    {id: 'regions', label: 'Places'},
    {id: 'challenge', label: 'Challenges'},
    {id: 'media', label: 'Media'}
];

export const ACHIEVEMENT_SET_DEFINITIONS: AchievementSetDefinition[] = [
    {
        id: 'species-explorer',
        title: 'Species Explorer',
        description: 'Discover and record unique species across your life list.',
        theme: 'species',
        metricId: 'species',
        color: '#5a7d43',
        icon: faBinoculars,
        milestones: [
            createMilestone('species', 50, 'Species', 'common', 80, 'First 50 Species'),
            createMilestone('species', 100, 'Species', 'common', 100),
            createMilestone('species', 200, 'Species', 'common', 130),
            createMilestone('species', 250, 'Species', 'common', 150),
            createMilestone('species', 300, 'Species', 'uncommon', 170),
            createMilestone('species', 400, 'Species', 'uncommon', 200),
            createMilestone('species', 500, 'Species', 'uncommon', 230),
            createMilestone('species', 600, 'Species', 'rare', 270),
            createMilestone('species', 750, 'Species', 'rare', 320),
            createMilestone('species', 1000, 'Species', 'rare', 380, '1,000 Species'),
            createMilestone('species', 1500, 'Species', 'epic', 470, '1,500 Species'),
            createMilestone('species', 2000, 'Species', 'epic', 580, '2,000 Species'),
            createMilestone('species', 2500, 'Species', 'epic', 700, '2,500 Species'),
            createMilestone('species', 3000, 'Species', 'legendary', 850, '3,000 Species'),
            createMilestone('species', 4000, 'Species', 'legendary', 1050, '4,000 Species'),
            createMilestone('species', 5000, 'Species', 'legendary', 1300, '5,000 Species'),
            createMilestone('species', 7500, 'Species', 'legendary', 1700, '7,500 Species'),
            createMilestone('species', 10000, 'Species', 'legendary', 2200, '10,000 Species')
        ]
    },
    {
        id: 'habitat-explorer',
        title: 'Habitat Explorer',
        description: 'Explore a wider spread of habitats across your birding life.',
        theme: 'habitat',
        metricId: 'habitats',
        color: '#567d6e',
        icon: faLeaf,
        milestones: [
            createMilestone('habitat', 5, 'Habitats', 'common', 60),
            createMilestone('habitat', 10, 'Habitats', 'common', 80),
            createMilestone('habitat', 15, 'Habitats', 'uncommon', 100),
            createMilestone('habitat', 20, 'Habitats', 'uncommon', 130),
            createMilestone('habitat', 25, 'Habitats', 'rare', 160),
            createMilestone('habitat', 30, 'Habitats', 'rare', 200),
            createMilestone('habitat', 35, 'Habitats', 'rare', 250),
            createMilestone('habitat', 40, 'Habitats', 'epic', 310),
            createMilestone('habitat', 45, 'Habitats', 'epic', 380),
            createMilestone('habitat', 50, 'Habitats', 'legendary', 460)
        ]
    },
    {
        id: 'family-finder',
        title: 'Family Finder',
        description: 'Find birds across more and more avian families.',
        theme: 'species',
        metricId: 'families',
        color: '#6d5a80',
        icon: faFeatherPointed,
        milestones: [
            createMilestone('family', 5, 'Families', 'common', 55),
            createMilestone('family', 10, 'Families', 'common', 70),
            createMilestone('family', 25, 'Families', 'uncommon', 110),
            createMilestone('family', 50, 'Families', 'uncommon', 160),
            createMilestone('family', 75, 'Families', 'rare', 220),
            createMilestone('family', 100, 'Families', 'rare', 290),
            createMilestone('family', 150, 'Families', 'epic', 380),
            createMilestone('family', 200, 'Families', 'epic', 500),
            createMilestone('family', 250, 'Families', 'legendary', 650)
        ]
    },
    {
        id: 'county-lister',
        title: 'County Lister',
        description: 'Expand your reach one county at a time.',
        theme: 'regions',
        metricId: 'counties',
        color: '#b56d2c',
        icon: faLocationDot,
        milestones: [
            createMilestone('county', 5, 'Counties', 'common', 60),
            createMilestone('county', 10, 'Counties', 'common', 80),
            createMilestone('county', 25, 'Counties', 'common', 110),
            createMilestone('county', 50, 'Counties', 'uncommon', 150),
            createMilestone('county', 100, 'Counties', 'uncommon', 220),
            createMilestone('county', 150, 'Counties', 'rare', 300),
            createMilestone('county', 200, 'Counties', 'rare', 390),
            createMilestone('county', 250, 'Counties', 'rare', 490),
            createMilestone('county', 300, 'Counties', 'epic', 600),
            createMilestone('county', 400, 'Counties', 'epic', 760),
            createMilestone('county', 500, 'Counties', 'legendary', 950),
            createMilestone('county', 750, 'Counties', 'legendary', 1250),
            createMilestone('county', 1000, 'Counties', 'legendary', 1650)
        ]
    },
    {
        id: 'world-traveler',
        title: 'World Traveler',
        description: 'Bird in countries near and far.',
        theme: 'regions',
        metricId: 'countries',
        color: '#3b7384',
        icon: faEarthAmericas,
        milestones: [
            createMilestone('country', 2, 'Countries', 'common', 70),
            createMilestone('country', 3, 'Countries', 'common', 90),
            createMilestone('country', 5, 'Countries', 'uncommon', 120),
            createMilestone('country', 10, 'Countries', 'uncommon', 170),
            createMilestone('country', 15, 'Countries', 'rare', 230),
            createMilestone('country', 20, 'Countries', 'rare', 300),
            createMilestone('country', 25, 'Countries', 'rare', 380),
            createMilestone('country', 50, 'Countries', 'epic', 520),
            createMilestone('country', 75, 'Countries', 'epic', 700),
            createMilestone('country', 100, 'Countries', 'legendary', 920),
            createMilestone('country', 150, 'Countries', 'legendary', 1250),
            createMilestone('country', 200, 'Countries', 'legendary', 1650)
        ]
    },
    {
        id: 'big-year-chaser',
        title: 'Big Year Chaser',
        description: 'Push your strongest year higher and higher.',
        theme: 'challenge',
        metricId: 'bigYearSpecies',
        color: '#9b5142',
        icon: faMountainSun,
        milestones: [
            createMilestone('bigyear', 50, 'Species Year', 'common', 60, '50 Species Year'),
            createMilestone('bigyear', 100, 'Species Year', 'common', 90, '100 Species Year'),
            createMilestone('bigyear', 150, 'Species Year', 'common', 120, '150 Species Year'),
            createMilestone('bigyear', 200, 'Species Year', 'uncommon', 160, '200 Species Year'),
            createMilestone('bigyear', 250, 'Species Year', 'uncommon', 210, '250 Species Year'),
            createMilestone('bigyear', 300, 'Species Year', 'uncommon', 270, '300 Species Year'),
            createMilestone('bigyear', 350, 'Species Year', 'rare', 340, '350 Species Year'),
            createMilestone('bigyear', 400, 'Species Year', 'rare', 420, '400 Species Year'),
            createMilestone('bigyear', 450, 'Species Year', 'rare', 510, '450 Species Year'),
            createMilestone('bigyear', 500, 'Species Year', 'epic', 610, '500 Species Year'),
            createMilestone('bigyear', 550, 'Species Year', 'epic', 720, '550 Species Year'),
            createMilestone('bigyear', 600, 'Species Year', 'epic', 840, '600 Species Year'),
            createMilestone('bigyear', 650, 'Species Year', 'epic', 970, '650 Species Year'),
            createMilestone('bigyear', 700, 'Species Year', 'epic', 1110, '700 Species Year'),
            createMilestone('bigyear', 750, 'Species Year', 'epic', 1260, '750 Species Year'),
            createMilestone('bigyear', 800, 'Species Year', 'legendary', 1420, '800 Species Year'),
            createMilestone('bigyear', 850, 'Species Year', 'legendary', 1590, '850 Species Year'),
            createMilestone('bigyear', 900, 'Species Year', 'legendary', 1770, '900 Species Year'),
            createMilestone('bigyear', 950, 'Species Year', 'legendary', 1960, '950 Species Year'),
            createMilestone('bigyear', 1000, 'Species Year', 'legendary', 2160, '1,000 Species Year'),
            createMilestone('bigyear', 1500, 'Species Year', 'legendary', 2700, '1,500 Species Year'),
            createMilestone('bigyear', 2000, 'Species Year', 'legendary', 3400, '2,000 Species Year')
        ]
    },
    {
        id: 'photographer',
        title: 'Photographer',
        description: 'Capture birds and memorable sightings with media.',
        theme: 'media',
        metricId: 'photoObservations',
        color: '#486575',
        icon: faCamera,
        milestones: [
            createMilestone('photo', 10, 'Photo Sightings', 'common', 70),
            createMilestone('photo', 25, 'Photo Sightings', 'common', 95),
            createMilestone('photo', 50, 'Photo Sightings', 'uncommon', 130),
            createMilestone('photo', 100, 'Photo Sightings', 'uncommon', 180),
            createMilestone('photo', 250, 'Photo Sightings', 'rare', 260),
            createMilestone('photo', 500, 'Photo Sightings', 'rare', 360),
            createMilestone('photo', 750, 'Photo Sightings', 'epic', 480),
            createMilestone('photo', 1000, 'Photo Sightings', 'epic', 620),
            createMilestone('photo', 1500, 'Photo Sightings', 'legendary', 820),
            createMilestone('photo', 2000, 'Photo Sightings', 'legendary', 1080)
        ]
    }
];

export const ACHIEVEMENT_RARITY_GUIDE: AchievementDashboard['rarityGuide'] = [
    {id: 'common', label: 'Common', description: 'Early milestones and easy wins.', color: '#6f8b52'},
    {id: 'uncommon', label: 'Uncommon', description: 'A bit more range and repetition.', color: '#4f7684'},
    {id: 'rare', label: 'Rare', description: 'Strong progress across a theme.', color: '#765f95'},
    {id: 'epic', label: 'Epic', description: 'A serious personal benchmark.', color: '#c6902c'},
    {id: 'legendary', label: 'Legendary', description: 'The hardest targets in the set.', color: '#a85a47'}
];
